import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { lloydConstants } from './Constants';
import {
	NO_COMPARISON,
	useFilterContext,
	useSelectionContext,
	useViewContext,
	useWeightContext,
	useYearContext,
} from './context';
import { indistinguishableCount, weightedUncertainty } from './stats';
import { scaleFor } from './scales';

export { divergingScale, categoricalScale } from './scales';

const useResizeObserver = (ref) => {
	const [dimensions, setDimensions] = useState(null);

	useEffect(() => {
		const observeTarget = ref.current;
		const resizeObserver = new ResizeObserver((entries) => {
			entries.forEach((entry) => setDimensions(entry.contentRect));
		});

		resizeObserver.observe(observeTarget);
		return () => resizeObserver.unobserve(observeTarget);
	}, [ref]);

	return dimensions;
};

const useReducedMotion = () => {
	const [reduced, setReduced] = useState(
		() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
	);

	useEffect(() => {
		const query = window.matchMedia('(prefers-reduced-motion: reduce)');
		const onChange = (event) => setReduced(event.matches);

		query.addEventListener('change', onChange);
		return () => query.removeEventListener('change', onChange);
	}, []);

	return reduced;
};

// Motion. The pointer wave is deliberately shallow and tight: across 139 heavily
// overlapping shapes, a broad or tall wave reads as nausea rather than as touch.
const WAVE_AMPLITUDE = 14;
const WAVE_SIGMA = 110;
const HOVER_LIFT = 18;
const HOVER_SCALE = 1.05;

// Left alone, the chart runs its own wave down the ranking so it reads as something
// to touch rather than a still image. Any interaction stops it at once.
const IDLE_AFTER_MS = 5000;
// Shapes sit about 5px apart down the stack, so a crest this tall carries a country
// clear of several neighbours and the wave reads as a travelling hump rather than a
// swell. Pointer response stays modest by comparison -- following the cursor wants
// precision, idling wants presence.
const IDLE_AMPLITUDE = 34;
const IDLE_SPEED = 14; // countries per second, averaged over one sweep
const IDLE_SPREAD = 16; // countries either side of the crest

function RiskIndexViz() {
	const filters = useFilterContext();
	const weights = useWeightContext();
	const selection = useSelectionContext();
	const reducedMotion = useReducedMotion();
	const { selectedYear, comparisonYear, yearlyData } = useYearContext();
	const { setView } = useViewContext();

	const [filteredData, setFilteredData] = useState({});
	const [defaultOrder, setDefaultOrder] = useState({});
	const [focusIndex, setFocusIndex] = useState(-1);

	const svgRef = useRef();
	const wrapperRef = useRef();
	const wrapDim = useResizeObserver(wrapperRef);
	const tooltip = d3.select('.ri-tooltip');
	const svg = d3.select(svgRef.current);
	const mainGroup = svg.select('#mainGroup');

	// Layout centres cached at draw time, so the pointer wave never reads the DOM.
	const layoutRef = useRef(new Map());
	const lastTapRef = useRef(null);
	const firstDrawRef = useRef(false);
	const pointerRef = useRef(null);
	const frameRef = useRef(null);
	const hoveredRef = useRef(null);
	const clearHoverRef = useRef(null);
	const lastInteractionRef = useRef(performance.now());
	const settledRef = useRef(false);

	const isComparing = comparisonYear !== NO_COMPARISON;
	const scales = useMemo(() => scaleFor(selection.colourBlindSafe), [selection.colourBlindSafe]);

	const weightTotal = weights.household + weights.community + weights.society + weights.individual;
	const weightingIsPublished =
		weights.household === weights.community &&
		weights.community === weights.society &&
		weights.society === weights.individual;

	useEffect(() => {
		d3.select('.ri-tooltip')
			.selectAll('.ri-tooltip-old-value,.ri-tooltip-old-year')
			.style('display', isComparing ? null : 'none');
	}, [isComparing]);

	const filterFunction = useCallback(
		(d) => {
			const matches = (key, field) =>
				!filters[key]?.length || filters[key].map((f) => +f.id).includes(d[field]);

			return (
				matches('gender', 'Gender') &&
				matches('ageGroup', 'AgeGroups4') &&
				matches('income', 'INCOME_5') &&
				matches('educationLevel', 'Education') &&
				matches('urbanicity', 'Urbanicity') &&
				matches('employmentStatus', 'EMP_2010')
			);
		},
		[filters]
	);

	const aggregateCountries = useCallback(
		(countryGrouped, respondentsByCode) => {
			// The composite is linear in the four dimensions, so scoring each respondent
			// and then averaging gives the same country score as averaging each dimension
			// first. It also yields a per-respondent spread, which a margin of error needs.
			const composite = (record) =>
				(record.Individual * weights.individual +
					record.Household * weights.household +
					record.Community * weights.community +
					record.Society * weights.society) /
				weightTotal;

			const processed = countryGrouped.map(([code, records]) => {
				const country = lloydConstants.countryCodeToName[code];
				const props = lloydConstants.countriesProps[country] ?? {};
				const incomeLevel = lloydConstants.reverseIncomeLevels[props.country_income] ?? 9;
				const weightSum = d3.sum(records, (record) => record.W);
				const weightedMeanOf = (accessor) =>
					d3.sum(records, (record) => record.W * accessor(record)) / weightSum;

				const { mean, moe, effectiveN } = weightedUncertainty(records, composite);

				return {
					country,
					incomeLevel,
					incomeLabel: lloydConstants.incomeLevels[incomeLevel],
					region: props.region,
					household: weightedMeanOf((record) => record.Household),
					community: weightedMeanOf((record) => record.Community),
					society: weightedMeanOf((record) => record.Society),
					individual: weightedMeanOf((record) => record.Individual),
					resilience: mean,
					moe,
					ciLow: mean - moe,
					ciHigh: mean + moe,
					effectiveN,
					count: records.length,
					total: respondentsByCode.get(code) ?? records.length,
					mfRatio: d3.sum(records, (record) => (record.Gender === 1 ? record.W : 0)) / weightSum,
				};
			});

			const ranked = processed.sort((a, b) => b.resilience - a.resilience);
			ranked.forEach((item) => {
				item.indistinguishable = indistinguishableCount(item, ranked);
			});

			return ranked;
		},
		[weights, weightTotal]
	);

	// A country filtered down to a handful of respondents cannot be ranked, and drawing
	// it anyway is the failure that matters here: the shape looks exactly as confident
	// as one built on a thousand people.
	const split = useCallback((ranked) => {
		const shown = ranked.filter((item) => item.count >= lloydConstants.minSampleSize);
		return { shown, withheld: ranked.length - shown.length };
	}, []);

	// Respondents per country, before any filter. Depends only on the data.
	const totalsByWave = useMemo(() => {
		const totals = {};
		Object.entries(yearlyData).forEach(([year, rows]) => {
			totals[year] = d3.rollup(rows, (records) => records.length, (d) => d.country_code);
		});
		return totals;
	}, [yearlyData]);

	// The unfiltered ranking changes with the data or the weights, not the filters,
	// so a filter change no longer re-aggregates every wave twice over.
	useEffect(() => {
		const unfiltered = {};
		Object.entries(yearlyData).forEach(([year, rows]) => {
			unfiltered[year] = aggregateCountries(d3.groups(rows, (d) => d.country_code), totalsByWave[year]);
		});
		setDefaultOrder(unfiltered);
	}, [aggregateCountries, yearlyData, totalsByWave]);

	// Filtering only has to cover the wave on screen and the one it is compared
	// against. Any other loaded wave is left until it is asked for.
	useEffect(() => {
		const wanted = [selectedYear, comparisonYear].filter((year) => yearlyData[year]);
		if (wanted.length === 0) return;

		setFilteredData((previous) => {
			const next = { ...previous };
			wanted.forEach((year) => {
				next[year] = aggregateCountries(
					d3.groups(yearlyData[year].filter(filterFunction), (d) => d.country_code),
					totalsByWave[year]
				);
			});
			return next;
		});
	}, [filterFunction, aggregateCountries, yearlyData, totalsByWave, selectedYear, comparisonYear]);

	const activeRanked = useMemo(() => filteredData[selectedYear] ?? [], [filteredData, selectedYear]);
	const { shown: activeDataset, withheld } = useMemo(() => split(activeRanked), [activeRanked, split]);

	const comparisonById = useMemo(() => {
		if (!isComparing) return new Map();
		const rows = filteredData[comparisonYear] ?? [];
		return new Map(rows.map((row) => [row.country, row]));
	}, [filteredData, comparisonYear, isComparing]);

	useEffect(() => {
		setView({ rows: activeDataset, withheld, weightingIsPublished, year: selectedYear });
	}, [activeDataset, withheld, weightingIsPublished, selectedYear, setView]);

	const describe = useCallback(
		(d) =>
			`${d.country}. Resilience index ${d3.format('.0f')(d.resilience)}, ` +
			`plus or minus ${d3.format('.1f')(d.moe)}. ` +
			`Household ${d3.format('.0f')(d.household)}, community ${d3.format('.0f')(d.community)}, ` +
			`society ${d3.format('.0f')(d.society)}, individual ${d3.format('.0f')(d.individual)}. ` +
			`${d3.format(',')(d.count)} respondents.`,
		[]
	);

	const fillFor = useCallback(
		(d) => {
			const modes = lloydConstants.colorBy;

			if (selection.colorBy === modes[2]) {
				return d.incomeLevel === 9 ? '#D7D7D7' : scales.categorical((d.incomeLevel - 1) / 4);
			}

			if (selection.colorBy === modes[3]) {
				const previous = comparisonById.get(d.country);
				return previous ? scales.change(d.resilience - previous.resilience) : '#D7D7D7';
			}

			if (selection.colorBy === modes[4]) return scales.precision(d.moe);

			return scales.resilience(d.resilience);
		},
		[selection.colorBy, scales, comparisonById]
	);

	const isDimmed = useCallback(
		(d) => {
			if (
				selection.incomeLevel.length > 0 &&
				selection.incomeLevel.findIndex((item) => +item.id === +d.incomeLevel) === -1
			) {
				return true;
			}

			return (
				selection.country.length > 0 &&
				selection.country.findIndex((item) => item.label === d.country) === -1
			);
		},
		[selection.country, selection.incomeLevel]
	);

	const toggleCountry = useCallback(
		(d) => {
			const country = lloydConstants.countriesProps[d.country];
			if (!country) return;

			const entry = { id: d.country, label: d.country, group: country.region };

			selection.setCountry((countries) => {
				const index = countries.findIndex((item) => item.id === entry.id);
				return index > -1 ? countries.filter((item) => item.id !== entry.id) : [...countries, entry];
			});
		},
		[selection]
	);

	const showTooltip = useCallback(
		(d) => {
			const format = d3.format('.0f');
			const setRow = (cls, value) =>
				tooltip.select(`.ri-tooltip-row.${cls}`).select('.ri-tooltip-value').text(value);

			tooltip.select('.ri-tooltip-title').text(d.country);
			setRow('income-group', lloydConstants.incomeLevels[d.incomeLevel]);
			setRow('global-region', d.region);

			['household', 'community', 'society', 'individual'].forEach((metric) => {
				setRow(metric, format(d[metric]));
				tooltip
					.select(`.ri-tooltip-row.${metric}`)
					.select('.ri-tooltip-color-value')
					.style('fill', scales.resilience(d[metric]));
			});

			setRow('resilience', format(d.resilience));
			setRow('uncertainty', `±${d3.format('.1f')(d.moe)}`);
			setRow(
				'indistinguishable',
				d.indistinguishable === 0
					? 'None at this precision'
					: `${d.indistinguishable} ${d.indistinguishable === 1 ? 'country' : 'countries'}`
			);
			setRow('total', d3.format(',')(d.total));
			setRow('filtered', d3.format(',')(d.count));
			setRow('gender', `${d3.format('.0%')(1 - d.mfRatio)} / ${d3.format('.0%')(d.mfRatio)}`);

			tooltip
				.select('.ri-tooltip-imprecise')
				.style('display', d.count < lloydConstants.impreciseSampleSize ? null : 'none');

			const comparisonOrder = isComparing ? defaultOrder[comparisonYear] ?? [] : [];
			const comparisonCountry = comparisonById.get(d.country);

			if (isComparing) {
				tooltip
					.select('.ri-tooltip-old-year')
					.text(
						comparisonCountry
							? `Compared with data from ${comparisonYear}`
							: `No data for ${comparisonYear}`
					);

				['resilience', 'household', 'community', 'society', 'individual'].forEach((metric) => {
					const value =
						metric === 'resilience' ? comparisonCountry?.resilience : comparisonCountry?.[metric];

					tooltip
						.select(`.ri-tooltip-row.${metric}`)
						.select('.ri-tooltip-old-value')
						.text(comparisonCountry ? format(value) : 'N/A');
				});
			} else {
				tooltip.select('.ri-tooltip-old-year').text('');
				tooltip.selectAll('.ri-tooltip-old-value').text('');
			}

			const currentOrder = defaultOrder[selectedYear] ?? [];
			const origPosition = currentOrder.findIndex((item) => item.country === d.country);

			tooltip
				.select('.ri-tooltip-row.original-position')
				.select('.ri-tooltip-value')
				.text(`${origPosition + 1} / ${currentOrder.length}`);

			if (isComparing) {
				const position = comparisonOrder.findIndex((item) => item.country === d.country);
				tooltip
					.select('.ri-tooltip-row.original-position')
					.select('.ri-tooltip-old-value')
					.text(position >= 0 ? `${position + 1} / ${comparisonOrder.length}` : 'N/A');
			}

			const filteredPosition = activeDataset.findIndex((item) => item.country === d.country);
			tooltip
				.select('.ri-tooltip-row.filtered-position')
				.select('.ri-tooltip-value')
				.text(`${filteredPosition + 1} / ${activeDataset.length}`);

			if (isComparing) {
				const comparisonFiltered = split(filteredData[comparisonYear] ?? []).shown;
				const position = comparisonFiltered.findIndex((item) => item.country === d.country);

				tooltip
					.select('.ri-tooltip-row.filtered-position')
					.select('.ri-tooltip-old-value')
					.text(position >= 0 ? `${position + 1} / ${comparisonFiltered.length}` : 'N/A');
			}

			tooltip.classed('is-visible', true);
		},
		[
			tooltip,
			scales,
			isComparing,
			comparisonYear,
			comparisonById,
			defaultOrder,
			selectedYear,
			activeDataset,
			filteredData,
			split,
		]
	);

	// --- drawing ----------------------------------------------------------------
	useEffect(() => {
		if (!wrapDim) return;

		const width = wrapDim.width;
		const height = wrapDim.height < 400 ? 400 : wrapDim.height;
		const count = Math.max(activeDataset.length, 1);

		svg.attr('width', '100%').attr('height', '100%');

		// Discs are sized for a desktop canvas; the same radii swamp a phone.
		const scale = Math.max(0.4, Math.min(1, width / 1100, height / 820));
		const offset = (width * 0.75) / count;
		const start = width / 2 + (activeDataset.length * offset) / 2 - 50 * scale;
		// The stack starts `count` pixels above initialPosition (see positionOf), a term
		// that does not shrink with the discs. Top it back up as the scale drops so the
		// first countries are not tucked under the header on a phone.
		const initialPosition = 250 * scale + activeDataset.length * (1 - scale);
		const verticalStep = (height * 0.7) / count;

		const radiusScale = d3.scalePow().domain([0, 100]).range([50 * scale, 200 * scale]);
		const angleSlice = (Math.PI * 2) / 4;

		// curveBasisClosed is the original look: a B-spline that smooths the four values
		// into a soft disc. It does not pass through them -- each axis renders at about
		// two thirds of its radius -- and an interpolating curve was tried and reverted
		// because it read as angular. The tooltip carries the exact figures.
		const radarLine = d3
			.lineRadial()
			.curve(d3.curveBasisClosed)
			.radius((d) => radiusScale(d))
			.angle((d, i) => i * angleSlice);

		const shapeOf = (d) => radarLine([d.household, d.community, d.society, d.individual]);
		const isLifted = (d) =>
			selection.country.length > 0 && selection.country.some((item) => item.label === d.country);
		const positionOf = (i, lifted) => [
			start - i * offset,
			initialPosition - activeDataset.length + i * verticalStep - (lifted ? 50 * scale : 0),
		];

		const speed = reducedMotion ? 0 : 1;
		const previousLayout = layoutRef.current;
		const shapesGroup = mainGroup.select('#shapes').attr('stroke', 'black').attr('stroke-width', 1);
		const slots = shapesGroup.selectAll('g.country-slot').data(activeDataset, (d) => d.country);

		const entering = slots
			.enter()
			.append('g')
			.attr('class', 'country-slot')
			.attr('transform', (d, i) => `translate(${positionOf(i, false)})`);

		entering
			.append('path')
			.attr('class', 'country')
			.attr('fill', fillFor)
			.attr('d', radarLine([50, 50, 50, 50]))
			.style('opacity', 0)
			.transition('enter')
			.duration(1400 * speed)
			// The cascade runs from the head of the ranking down. The previous expression,
			// `all.length - i * 5`, ran backwards and flattened to zero after 28 shapes,
			// so most of the chart arrived at once.
			.delay((_, i) => i * 9 * speed)
			.ease(d3.easeCubicOut)
			.style('opacity', 1)
			.style('pointer-events', null)
			.attr('d', shapeOf);

		const merged = slots.merge(entering);

		// Delay by how far a country actually moves, so reordering reads as a sort
		// settling rather than every shape leaving at once.
		merged
			.transition('layout')
			.duration(700 * speed)
			.delay((d, i) => {
				const previous = previousLayout.get(d.country);
				return Math.min(previous ? Math.abs(previous.index - i) * 6 : 0, 260) * speed;
			})
			.ease(d3.easeCubicInOut)
			.attr('transform', (d, i) => `translate(${positionOf(i, isLifted(d))})`);

		// Rebind every draw rather than only on enter. These handlers close over
		// showTooltip, which changes as each wave finishes loading -- a shape that
		// entered before the comparison wave arrived would otherwise keep reporting
		// "no data" for it forever.
		merged
			.select('path')
			.attr('aria-label', describe)
			.on('pointerout', handlePointerOut)
			.on('pointerover', handlePointerOver)
			.on('pointerdown', (event, d) => {
				// No hover on touch: the first tap shows a country, a second tap on the
				// same one selects it. A mouse click still selects straight away.
				if (event.pointerType === 'touch' && lastTapRef.current !== d.country) {
					lastTapRef.current = d.country;
					return;
				}
				lastTapRef.current = null;
				toggleCountry(d);
			});

		merged
			.select('path')
			.transition('style')
			.duration(320 * speed)
			.ease(d3.easeCubicOut)
			.attr('fill', (d) => (isDimmed(d) ? 'white' : fillFor(d)))
			.attr('d', shapeOf)
			.style('opacity', 1)
			.style('pointer-events', null);

		merged.each(function () {
			d3.select(this).raise();
		});

		slots
			.exit()
			.transition('exit')
			.duration(420 * speed)
			.ease(d3.easeCubicIn)
			.style('opacity', 0)
			.style('pointer-events', 'none')
			.remove();

		// The resting z-order is rank order: `merged.each(raise)` above walks the data in
		// rank order, so the lowest ranked country ends up drawn on top. Stamping it lets
		// hover restore the stack exactly, rather than guessing from the score.
		activeDataset.forEach((d, i) => {
			d.stackOrder = i;
		});

		const layout = new Map();
		activeDataset.forEach((d, i) => {
			const [x, y] = positionOf(i, false);
			layout.set(d.country, { index: i, x, y });
		});
		layoutRef.current = layout;

		if (activeDataset.length > 0 && !firstDrawRef.current) {
			firstDrawRef.current = true;
			performance.mark('resilience:first-draw');
		}

		// Restoring rank order re-inserts every node, and doing that under a stationary
		// pointer makes the browser drop the pointerout for whatever was underneath. So
		// clear state by assignment over the whole selection rather than by trusting a
		// matching pair of events to arrive.
		const restoreOrder = () =>
			shapesGroup.selectAll('g.country-slot').sort((a, b) => a.stackOrder - b.stackOrder);


		const clearHover = () => {
			shapesGroup.selectAll('path.country').classed('highlight', false);
			hoveredRef.current = null;
			lastTapRef.current = null;
			tooltip.classed('is-visible', false);
			restoreOrder();
		};

		clearHoverRef.current = clearHover;

		function handlePointerOut(event) {
			// On touch the pointer "leaves" the moment the finger lifts, which would hide
			// the tooltip a tap just opened. Touch clears on the next tap elsewhere.
			if (event.pointerType === 'touch') return;
			clearHover();
		}

		function handlePointerOver(event, d) {
			restoreOrder();
			shapesGroup.selectAll('path.country').classed('highlight', false);
			d3.select(this).classed('highlight', true);
			hoveredRef.current = d.country;

			// Hovering marks a country and lifts it proud of its neighbours, but leaves it
			// in the deck. Only a country that has been selected is pulled clear of the
			// deck, so its whole shape reads.
			if (isLifted(d)) d3.select(this.parentNode).raise();

			showTooltip(d);
		}

		svg.on('pointerleave.stack', (event) => {
			if (event.pointerType !== 'touch') clearHover();
		});
	}, [wrapDim, selectedYear, activeDataset, selection, fillFor, isDimmed, reducedMotion, describe, showTooltip, toggleCountry]);

	// --- the pointer wave -------------------------------------------------------
	// Shapes lift as the pointer passes, with a Gaussian falloff along the stack.
	// Driven straight from requestAnimationFrame rather than through transitions,
	// which would queue and fight each other at pointer rates.
	useEffect(() => {
		const node = svgRef.current;
		if (!node || reducedMotion) return undefined;

		const onPointerMove = (event) => {
			const rect = node.getBoundingClientRect();
			pointerRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
		};

		const noteInteraction = () => {
			lastInteractionRef.current = performance.now();
		};

		const onPointerLeave = (event) => {
			pointerRef.current = null;
			// A pointerout can be swallowed when the stack is reordered underneath the
			// cursor, which used to strand the last shape highlighted. This is the
			// backstop: whatever happened, leaving the chart clears it. Not on touch,
			// where leaving is just the finger lifting.
			if (event.pointerType !== 'touch') clearHoverRef.current?.();
		};

		const tick = () => {
			const pointer = pointerRef.current;
			const now = performance.now();
			const idle = !pointer && now - lastInteractionRef.current > IDLE_AFTER_MS;
			const total = layoutRef.current.size;

			// The idle crest travels by rank rather than by pixel distance, so the wave
			// runs cleanly down the deck instead of tracking a phantom cursor. A cosine
			// carries it down and back rather than wrapping, easing it to a stop at each
			// end so the turn reads as a change of direction and not a jump.
			let crest = null;
			if (idle && total > 1) {
				const span = total - 1;
				const period = (2 * span) / IDLE_SPEED;
				const phase = ((now / 1000) % period) / period;
				crest = (span / 2) * (1 - Math.cos(phase * Math.PI * 2));
			}

			// Once every shape is at rest and nothing is driving it, there is nothing to
			// write. The loop keeps ticking so it can pick up the next pointer or the idle
			// timer, but it stops touching the DOM, which is what costs on a laptop.
			const active = Boolean(pointer) || crest !== null || Boolean(hoveredRef.current);
			if (!active && settledRef.current) {
				frameRef.current = requestAnimationFrame(tick);
				return;
			}

			let anyLifted = false;
			const slots = node.querySelectorAll('g.country-slot');

			slots.forEach((slot) => {
				const path = slot.firstChild;
				const datum = slot.__data__;
				if (!path || !datum) return;

				const layout = layoutRef.current.get(datum.country);
				if (!layout) return;

				let lift = 0;
				if (pointer) {
					const distance = Math.hypot(pointer.x - layout.x, pointer.y - layout.y);
					lift = WAVE_AMPLITUDE * Math.exp(-((distance / WAVE_SIGMA) ** 2));
				} else if (crest !== null) {
					lift = IDLE_AMPLITUDE * Math.exp(-(((layout.index - crest) / IDLE_SPREAD) ** 2));
				}

				const hovered = hoveredRef.current === datum.country;
				const total = lift + (hovered ? HOVER_LIFT : 0);
				if (total > 0.01) anyLifted = true;

				const transform = `translateY(${(-total).toFixed(2)}px) scale(${hovered ? HOVER_SCALE : 1})`;
				if (path.__transform !== transform) {
					path.__transform = transform;
					path.style.transform = transform;
				}
			});

			settledRef.current = !active && !anyLifted;
			frameRef.current = requestAnimationFrame(tick);
		};

		// The svg draws with overflow visible, so shapes extend past its box and a
		// pointerleave on the svg alone depends on which way the cursor happens to
		// exit. Watching the document instead means any move onto something that is
		// not a country releases the hover, whatever route the cursor took.
		const onDocumentMove = (event) => {
			noteInteraction();

			if (!event.target.closest?.('#shapes path.country')) clearHoverRef.current?.();
			// Only give up the pointer once it is genuinely off the chart; the shapes
			// overflow the svg box, so containment is the reliable test.
			if (!node.contains(event.target)) pointerRef.current = null;
		};

		node.addEventListener('pointermove', onPointerMove);
		node.addEventListener('pointerleave', onPointerLeave);
		document.addEventListener('pointermove', onDocumentMove, true);
		// A tap or click anywhere that is not a country releases the hover. This is
		// how touch dismisses a tooltip, since it has no pointer to move away.
		const onDocumentDown = (event) => {
			noteInteraction();
			if (!event.target.closest?.('#shapes path.country')) clearHoverRef.current?.();
		};

		document.addEventListener('pointerdown', onDocumentDown, true);
		document.addEventListener('keydown', noteInteraction, true);
		document.addEventListener('wheel', noteInteraction, { capture: true, passive: true });
		frameRef.current = requestAnimationFrame(tick);

		return () => {
			node.removeEventListener('pointermove', onPointerMove);
			node.removeEventListener('pointerleave', onPointerLeave);
			document.removeEventListener('pointermove', onDocumentMove, true);
			document.removeEventListener('pointerdown', onDocumentDown, true);
			document.removeEventListener('keydown', noteInteraction, true);
			document.removeEventListener('wheel', noteInteraction, { capture: true });
			if (frameRef.current) cancelAnimationFrame(frameRef.current);
		};
	}, [reducedMotion]);

	// --- keyboard ---------------------------------------------------------------
	// One tab stop onto the chart, then arrow keys walk the ranking. Tabbing through
	// 139 shapes individually would be its own accessibility failure.
	const moveFocus = useCallback(
		(delta) => {
			if (activeDataset.length === 0) return;

			setFocusIndex((current) => {
				const next = Math.max(
					0,
					Math.min(activeDataset.length - 1, current < 0 ? 0 : current + delta)
				);
				const datum = activeDataset[next];

				if (datum) {
					hoveredRef.current = datum.country;
					showTooltip(datum);
					d3.select(svgRef.current)
						.selectAll('g.country-slot')
						.classed('is-focused', (d) => d.country === datum.country);
				}

				return next;
			});
		},
		[activeDataset, showTooltip]
	);

	const clearFocus = useCallback(() => {
		hoveredRef.current = null;
		tooltip.classed('is-visible', false);
		setFocusIndex(-1);
		d3.select(svgRef.current).selectAll('g.country-slot').classed('is-focused', false);
	}, [tooltip]);

	const handleKeyDown = (event) => {
		const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];

		if (step) {
			event.preventDefault();
			moveFocus(step);
			return;
		}

		if (event.key === 'Home') {
			event.preventDefault();
			setFocusIndex(-1);
			moveFocus(1);
			return;
		}

		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			const datum = activeDataset[focusIndex];
			if (datum) toggleCountry(datum);
			return;
		}

		if (event.key === 'Escape') clearFocus();
	};

	const focused = activeDataset[focusIndex];

	return (
		<div ref={wrapperRef} className="viz-wrapper">
			<svg
				ref={svgRef}
				tabIndex={0}
				role="application"
				aria-label={
					`Resilience Index for ${selectedYear}. ` +
					`${activeDataset.length} countries ranked highest to lowest. ` +
					`Use the arrow keys to move through the ranking, Enter to select a country.`
				}
				onKeyDown={handleKeyDown}
				onBlur={clearFocus}
			>
				<g id="mainGroup">
					<g id="shapes" />
					<g id="example" />
				</g>
			</svg>

			{activeDataset.length === 0 && withheld > 0 && (
				<div className="viz-empty">
					<span className="material-symbols-outlined">filter_alt_off</span>
					<h2>No country has enough respondents left</h2>
					<p>
						{`These filters leave every one of the ${withheld} countries with fewer than ` +
							`${lloydConstants.minSampleSize} respondents. At that size the margin of error is ` +
							`wider than the gap between the highest and lowest scoring countries, so a ranking ` +
							`would be noise.`}
					</p>
					<p className="viz-empty-hint">Remove a filter to bring countries back.</p>
				</div>
			)}

			<div className="sr-only" aria-live="polite">
				{focused ? describe(focused) : ''}
			</div>

			<table className="sr-only">
				<caption>{`Resilience Index ${selectedYear}, ranked highest to lowest`}</caption>
				<thead>
					<tr>
						<th scope="col">Rank</th>
						<th scope="col">Country</th>
						<th scope="col">Resilience index</th>
						<th scope="col">Margin of error</th>
						<th scope="col">Respondents</th>
					</tr>
				</thead>
				<tbody>
					{activeDataset.map((d, i) => (
						<tr key={d.country}>
							<td>{i + 1}</td>
							<th scope="row">{d.country}</th>
							<td>{d3.format('.0f')(d.resilience)}</td>
							<td>{`±${d3.format('.1f')(d.moe)}`}</td>
							<td>{d3.format(',')(d.count)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export default RiskIndexViz;
